import { navModelFor } from '@titan/modules';
import { routeDecision } from '@titan/shared';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession } from '../session/SessionProvider.js';
import { Loading } from '../shell/Shell.js';

/** Anonymous visitors go to sign-in. */
export function RequireSession({ children }: { children?: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') return <Navigate to="/auth/sign-in" replace state={{ from: location.pathname }} />;
  return children ? <>{children}</> : <Outlet />;
}

/** Signed-in visitors never see the auth screens. */
export function RequireAnonymous({ children }: { children?: ReactNode }) {
  const { status } = useSession();
  if (status === 'loading') return <Loading />;
  if (status === 'ready') return <Navigate to="/" replace />;
  return children ? <>{children}</> : <Outlet />;
}

/**
 * Route gating from the same rules as the nav. Direct URL entry to an unentitled module
 * lands on the launcher; staff administration is unreachable under impersonation.
 */
export function Gate() {
  const { me } = useSession();
  const { pathname } = useLocation();
  if (!me) return null;
  const decision = routeDecision(me.actor, pathname, navModelFor(me.actor));
  if (!decision.allow) return <Navigate to={decision.redirectTo} replace />;
  return <Outlet />;
}
